import React from "react";

import moment from "moment";

import { isValidTime } from "../../utils/timeUtils";
import { InputMode } from "../Input/types";
import { IOptionItemProps } from "../Select/Option";
import { ElementWithRef, Select } from "../Select/Select";
import { SelectTrigger, SelectTriggerProps } from "../SelectTrigger";

export type Time24HourFormat = `${string}:${string}` | undefined;

export interface SelectTimeProps extends Omit<SelectTriggerProps, "mode"> {
  mode?: "12" | "24";
  selected?: Time24HourFormat;
  min?: Time24HourFormat;
  max?: Time24HourFormat;
  onChange?: () => void;
  target?: ElementWithRef<Element>;
  className?: string;
  triggerMode?: InputMode;
  locale?: string;
  trigger?: string;
  step?: 1 | 5 | 15 | 30 | 60;
}

const renderOption = (option) => {
  return <div id={option.id}>{option.name}</div>;
};

export const SelectTime: React.FC<SelectTimeProps> = ({
  mode = "24",
  selected,
  min = "00:00",
  max = "23:45",
  onChange,
  target,
  triggerMode,
  step = 15,
  ...rest
}) => {
  const generateTimeOptions = (): IOptionItemProps[] => {
    const minTime = moment(isValidTime(min) ? min : "00:00", "HH:mm");
    const maxTime = moment(isValidTime(max) ? max : "23:45", "HH:mm");
    const options: IOptionItemProps[] = [];

    const currentTime = minTime.clone();
    while (currentTime.isSameOrBefore(maxTime)) {
      options.push({
        id: currentTime.format("HH:mm"),
        name: currentTime.format(mode === "12" ? "hh:mm A" : "HH:mm"),
      });
      currentTime.add(step, "minutes");
    }

    return options;
  };

  const timeOptions = generateTimeOptions();

  const displaySelectedTime = moment(selected, "HH:mm").format(
    mode === "12" ? "hh:mm A" : "HH:mm"
  );

  const handleScrollToSelected = () => {
    if (selected) {
      setTimeout(() => {
        const selectedItem = document.getElementById(selected)?.parentElement;
        selectedItem?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  };

  return (
    <Select
      options={timeOptions}
      disabledInternalSort
      selected={selected}
      disableSearch
      keepSameOptionsOrder
      target={
        target ? (
          target
        ) : (
          <SelectTrigger mode={triggerMode} {...rest}>
            {displaySelectedTime}
          </SelectTrigger>
        )
      }
      mode="tiny"
      renderOption={renderOption}
      onChange={onChange}
      forceCloseMenu
      onSelectOpen={handleScrollToSelected}
    />
  );
};
